/**
 * Weeks and hatching (docs/SPEC.md section 2). A week runs from Monday 00:00 to the next Monday 00:00 in the player's
 * local time zone, numbered like ISO weeks ("2026-W43"). The time zone is locked at hatch, so all functions take it as
 * an argument. Uses only Intl, no date library. In a week with a clock change the week is 167 or 169 hours long.
 */

const DAY_MS = 86_400_000;

interface Local {
  y: number;
  m: number;
  d: number;
  hh: number;
  mm: number;
  ss: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** The wall-clock date and time in the zone at a real instant. */
function localParts(at: number, timeZone: string): Local {
  const out: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(at))) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return { y: out.year as number, m: out.month as number, d: out.day as number, hh: out.hour as number, mm: out.minute as number, ss: out.second as number };
}

/** The real instant at which the wall clock in the zone reads the given local date and time. */
function zonedToInstant(y: number, m: number, d: number, timeZone: string): number {
  const asUtc = Date.UTC(y, m - 1, d, 0, 0, 0);
  // Find the zone's offset near that time, then correct once (handles an offset change on the day itself).
  let guess = asUtc;
  for (let i = 0; i < 3; i++) {
    const p = localParts(guess, timeZone);
    const shown = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss);
    const diff = shown - asUtc;
    if (diff === 0) break;
    guess -= diff;
  }
  return guess;
}

/** ISO weekday of a calendar date: Monday = 1 ... Sunday = 7. */
function isoWeekday(y: number, m: number, d: number): number {
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 ? 7 : wd;
}

/** The ISO week id of a calendar date, for example "2026-W43". */
function isoWeekId(y: number, m: number, d: number): string {
  const date = new Date(Date.UTC(y, m - 1, d));
  // The Thursday of this week decides which year the week belongs to.
  date.setUTCDate(date.getUTCDate() + (4 - isoWeekday(y, m, d)));
  const year = date.getUTCFullYear();
  const dayOfYear = Math.floor((date.getTime() - Date.UTC(year, 0, 1)) / DAY_MS);
  const week = Math.floor(dayOfYear / 7) + 1;
  return `${year}-W${String(week).padStart(2, "0")}`;
}

export function weekIdFor(at: number, timeZone: string): string {
  const p = localParts(at, timeZone);
  return isoWeekId(p.y, p.m, p.d);
}

export interface WeekBounds {
  /** Monday 00:00 local, as a real instant (milliseconds). */
  start: number;
  /** The next Monday 00:00 local. */
  end: number;
  /** The length of this week in real minutes: 10,080, or 10,020 or 10,140 across a clock change. */
  minutes: number;
}

export function weekBounds(weekId: string, timeZone: string): WeekBounds {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekId);
  if (!match) throw new Error(`Invalid week id: ${weekId}`);
  const year = Number(match[1]);
  const week = Number(match[2]);
  // Week 1 is the week that contains January 4th.
  const monday1 = Date.UTC(year, 0, 4 - (isoWeekday(year, 1, 4) - 1));
  const mondayDate = new Date(monday1 + (week - 1) * 7 * DAY_MS);
  const nextDate = new Date(mondayDate.getTime() + 7 * DAY_MS);
  const start = zonedToInstant(mondayDate.getUTCFullYear(), mondayDate.getUTCMonth() + 1, mondayDate.getUTCDate(), timeZone);
  const end = zonedToInstant(nextDate.getUTCFullYear(), nextDate.getUTCMonth() + 1, nextDate.getUTCDate(), timeZone);
  return { start, end, minutes: Math.round((end - start) / 60_000) };
}

/** Whole weeks from week `a` to week `b` (negative if `b` is earlier). Counted on UTC Mondays, so year ends and clock changes do not matter. */
export function weeksBetween(a: string, b: string): number {
  return Math.round((weekBounds(b, "UTC").start - weekBounds(a, "UTC").start) / (7 * DAY_MS));
}

/** True on the first app open of a new week: the player has not hatched a pet in the current week yet. */
export function canHatch(lastHatchedWeekId: string | null, now: number, timeZone: string): boolean {
  return lastHatchedWeekId !== weekIdFor(now, timeZone);
}
